import {
  Button,
  Divider,
  Grid,
  Text,
  useToggle,
} from "@pushpress/shared-components";
import { useCallback, useContext, useMemo } from "react";
import { Control, useWatch } from "react-hook-form";
import { SwitchPlanForm, getBillingIntervalType } from "./SwitchPlanForm.utils";
import { CurrentUserSubscriptionFragment } from "../SwitchPlanDrawer-generated.hooks";
import {
  defaultToastOptions,
  extractError,
  formatToUSD,
} from "../../../utils/misc";
import { AppContext } from "../../../context/app";
import SectionTitleAndDescription from "./components/SectionTitleAndDescription";
import { formatInTimeZone } from "date-fns-tz";
import { getDiscountDescription } from "../../../components/controllers/DiscountSelect/DiscountSelect.utils";
import UserBillingSelect from "../../../components/controllers/UserBillingSelect";
import {
  CASH_BIL_UUID,
  COMP_UUID,
  getBillingUuid,
} from "../../../components/controllers/UserBillingSelect/UserBillingSelect.utils";
import PriceBreakdownForPlan from "./components/PriceBreakdownForPlan";
import PriceBreakdownToSwitchPlan from "./components/PriceBreakdownToSwitchPlan";
import { switchPlanFormStyles } from "./SwitchPlanForm.styles";
import { toast } from "react-toastify";
import { useGetInvoicePlanPreviewQuery } from "../../../apis/billingApi.hooks";
import { useSubscriptionMigratePreviewQuery } from "../../../apis/subscriptionApi.hooks";
import { InvoicePreview } from "src/apis/billingApi";

type SelectPaymentMethodStepProps = {
  control: Control<SwitchPlanForm>;
  goToNextStep: VoidFunction;
  goBack: VoidFunction;
  userSubscription: CurrentUserSubscriptionFragment;
  userUuid: string;
  loading: boolean;
};

const whenMap = {
  NextBillDate: "nextBillDate",
  Now: "now",
} as const;

function SelectPaymentMethodStep({
  control,
  goToNextStep,
  goBack,
  userSubscription,
  userUuid,
  loading,
}: SelectPaymentMethodStepProps) {
  const { client } = useContext(AppContext);
  const newPlan = useWatch({ control: control, name: "newPlan" });
  const planPrice = useWatch({ control: control, name: "planPrice" });
  const discount = useWatch({ control: control, name: "discount" });
  const whenToSwitch = useWatch({ control: control, name: "whenToSwitch" });
  const paymentMethod = useWatch({ control: control, name: "paymentMethod" });
  const setupFee = useWatch({ control: control, name: "setupFee" });
  const [showSwitchForPlanBreakdown, showSwitchForPlanBreakdownActions] =
    useToggle(false);
  const [showSwitchToSwitchPlan, showSwitchToSwitchPlanActions] =
    useToggle(false);
  const isComp = paymentMethod?.uuid === COMP_UUID;

  const handleError = useCallback((err: unknown) => {
    toast.error(extractError(err), defaultToastOptions);
  }, []);

  const {
    data: subscriptionMigratePreview,
    isLoading: isSubscriptionMigratePreviewLoading,
  } = useSubscriptionMigratePreviewQuery(
    {
      subscriptionUuid: userSubscription.uuid,
      newPlanUuid: newPlan.planUuid,
      planPriceUuid: planPrice?.planPriceUuid,
      discountUuid: discount?.discountUuid,
      billingUuid: paymentMethod?.uuid
        ? getBillingUuid(paymentMethod?.uuid)
        : CASH_BIL_UUID,
      when: whenMap[whenToSwitch],
      customSetupFee: setupFee,
    },
    {
      enabled: !isComp,
      onError: handleError,
    },
  );

  const { data: invoicePreviewTmp, isLoading: isInvoicePreviewLoading } =
    useGetInvoicePlanPreviewQuery(
      {
        planUuid: newPlan.planUuid,
        planPriceUuid: planPrice?.planPriceUuid,
        billingUuid: paymentMethod?.uuid
          ? getBillingUuid(paymentMethod?.uuid)
          : CASH_BIL_UUID,
        discountUuid: discount?.discountUuid,
        ignoreSetupFee: true,
        customSetupFee: setupFee,
        startDate:
          whenToSwitch === "NextBillDate"
            ? userSubscription.nextCharge
            : undefined,
      },
      {
        enabled: !isComp,
        onError: handleError,
      },
    );

  const invoicePreview = useMemo(() => {
    if (invoicePreviewTmp) {
      return sanitizeInvoicePreview(invoicePreviewTmp);
    }
    return null;
  }, [invoicePreviewTmp]);

  const switchDate =
    whenToSwitch === "NextBillDate"
      ? formatInTimeZone(
          new Date(userSubscription.nextCharge),
          "UTC",
          "MMMM d, yyyy",
        )
      : "Now";

  const amount = planPrice ? planPrice?.amount : Number(newPlan?.amount);
  const intervalType = planPrice ? planPrice?.intervalType : newPlan?.intervalType;
  const interval = planPrice ? planPrice?.interval : newPlan?.interval;

  return (
    <>
      <Grid container>
        <SectionTitleAndDescription
          title="Current plan"
          description={`
              ${userSubscription.plan.name} (${formatToUSD(
                userSubscription.amount,
                client.currencyIso,
              )} ${getBillingIntervalType(
                userSubscription.intervalType,
                userSubscription.chargeInterval,
              )})
                `}
        />
        <Grid item xs={12}>
          <Divider vPadding={16} />
        </Grid>
        <SectionTitleAndDescription
          title="New plan"
          description={`
                  ${newPlan.name} (${formatToUSD(
                    amount,
                    client.currencyIso,
                  )} ${getBillingIntervalType(
                    intervalType,
                    interval,
                  )})
                `}
        />
        <Grid item xs={12}>
          <Divider vPadding={16} />
        </Grid>
        {discount && (
          <>
            <SectionTitleAndDescription
              title="Discount"
              description={`
                ${discount.name} (${getDiscountDescription(
                  discount,
                  client.currencyIso,
                )})`}
            />
            <Grid item xs={12}>
              <Divider vPadding={16} />
            </Grid>
          </>
        )}
        <SectionTitleAndDescription
          title="Setup fee"
          description={formatToUSD(setupFee, client.currencyIso)}
        />
        <Grid item xs={12}>
          <Divider vPadding={16} />
        </Grid>
        <SectionTitleAndDescription
          title="Switch"
          description={
            whenToSwitch === "NextBillDate"
              ? formatInTimeZone(
                  new Date(userSubscription.nextCharge),
                  "UTC",
                  "MMMM d, yyyy",
                )
              : "Now"
          }
        />
      </Grid>
      <Divider vPadding={16} />
      <Grid container>
        <Grid item xs={12}>
          <Text
            variant="heading-5"
            color="wolf-800"
            weight="600"
            className="pb-3"
          >
            Payment method & billing summary
          </Text>
          <UserBillingSelect
            userUuid={userUuid}
            control={control}
            name="paymentMethod"
            label="Payment Method"
            hideLabel
            menuPortalTarget={document.body}
            required
          />
          <Grid container item xs={12}>
            <div className="p-3" style={switchPlanFormStyles.summaryBox}>
              {!isComp && (
                <>
                  <Grid container>
                    <Grid
                      container
                      item
                      xs={9}
                      justifyContent="center"
                      direction="column"
                    >
                      <Text
                        variant="heading-3"
                        color="wolf-800"
                        weight="600"
                        loading={isSubscriptionMigratePreviewLoading}
                        loadingCharacters={6}
                      >
                        {formatToUSD(
                          subscriptionMigratePreview?.total,
                          client.currencyIso,
                        )}
                      </Text>
                      <Text variant="body-lg" color="wolf-600" className="mt-2">
                        First bill ({switchDate})
                      </Text>
                    </Grid>
                    <Grid container item xs={3} justifyContent="flex-end">
                      {!isSubscriptionMigratePreviewLoading && (
                        <>
                          {subscriptionMigratePreview && (
                            <PriceBreakdownToSwitchPlan
                              visible={showSwitchToSwitchPlan}
                              subscriptionMigratePreview={
                                subscriptionMigratePreview
                              }
                              planOption={newPlan}
                              planPrice={planPrice}
                              discountOption={discount}
                              onClose={showSwitchToSwitchPlanActions.off}
                            />
                          )}
                          <Button
                            buttonType="text"
                            text="More info"
                            aria-label="More info to switch plan"
                            onClick={showSwitchToSwitchPlanActions.on}
                          />
                        </>
                      )}
                    </Grid>
                  </Grid>
                  <Divider vPadding={12} />
                  <Grid container>
                    <Grid
                      container
                      item
                      xs={9}
                      justifyContent="center"
                      direction="column"
                    >
                      <Text
                        variant="heading-3"
                        color="wolf-800"
                        weight="600"
                        loading={isInvoicePreviewLoading}
                        loadingCharacters={6}
                      >
                        {formatToUSD(invoicePreview?.total, client.currencyIso)}
                      </Text>
                      <Text variant="body-lg" color="wolf-600" className="mt-2">
                        Recurring bill
                      </Text>
                    </Grid>
                    <Grid container item xs={3} justifyContent="flex-end">
                      {!isInvoicePreviewLoading && (
                        <>
                          {invoicePreview && (
                            <PriceBreakdownForPlan
                              visible={showSwitchForPlanBreakdown}
                              invoicePreviewForPlan={invoicePreview}
                              discountOption={discount}
                              planOption={newPlan}
                              planPrice={planPrice}
                              onClose={showSwitchForPlanBreakdownActions.off}
                            />
                          )}
                          <Button
                            buttonType="text"
                            text="More info"
                            onClick={showSwitchForPlanBreakdownActions.on}
                            aria-label="More info to new plan"
                          />
                        </>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}
              {isComp && (
                <Grid
                  container
                  item
                  xs={12}
                  justifyContent="center"
                  direction="column"
                >
                  <Text variant="heading-3" color="wolf-800" weight="600">
                    No charge
                  </Text>
                  <Text variant="body-lg" color="wolf-600" className="mt-2">
                    All bills will be comped
                  </Text>
                </Grid>
              )}
            </div>
          </Grid>
        </Grid>
      </Grid>
      <Grid container item xs={12}>
        <Divider vPadding={16} />
      </Grid>
      <Grid container justifyContent="space-between">
        <Grid item>
          <Button buttonType="text" text="Back" onClick={goBack} />
        </Grid>
        <Grid item>
          <Button
            buttonType="primary"
            text="Save"
            onClick={goToNextStep}
            loading={loading}
            disabled={
              isInvoicePreviewLoading ||
              isSubscriptionMigratePreviewLoading ||
              !paymentMethod?.uuid
            }
          />
        </Grid>
      </Grid>
    </>
  );
}

// Get invoicePreview into a standard format regardless of fixed billing date or not
function sanitizeInvoicePreview(
  invoicePreviewTmp: InvoicePreview,
): InvoicePreview {
  return {
    ...invoicePreviewTmp,
    total: invoicePreviewTmp.totalRecurring ?? invoicePreviewTmp.total,
    subtotal: invoicePreviewTmp.subtotalRecurring ?? invoicePreviewTmp.subtotal,
    totalLessTxFeesAndTaxes:
      invoicePreviewTmp.totalRecurringLessTxFeesAndTaxes ??
      invoicePreviewTmp.totalLessTxFeesAndTaxes,
    discount: invoicePreviewTmp.discountRecurring ?? invoicePreviewTmp.discount,
    tax: invoicePreviewTmp.taxRecurring ?? invoicePreviewTmp.tax,
    fees: invoicePreviewTmp.feesRecurring ?? invoicePreviewTmp.fees,
  };
}

export default SelectPaymentMethodStep;
